/**
 * Applying the theme choice. `data-theme` on <html> drives the palette (styles.css); the
 * browser chrome reads <meta name="theme-color">, so that is kept in step too.
 *
 * The choice lives in settings (IndexedDB), and is mirrored to localStorage so main.tsx can
 * apply it before the first paint, without waiting for the database.
 */
import type { ThemeChoice } from '../store/settings'

export const THEME_KEY = 'resupport.theme'

/** Must track `--rs-bg` in styles.css. */
const BACKGROUND: Record<'light' | 'dark', string> = {
  light: '#f4f2ec',
  dark: '#111411',
}

export function readThemeMirror(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    // Storage denied: the default is fine.
  }
  return 'system'
}

export function effectiveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(choice: ThemeChoice): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
  try {
    localStorage.setItem(THEME_KEY, choice)
  } catch {
    // Non-fatal.
  }
  const meta = document.querySelector<HTMLMetaElement>('meta#rs-theme-color')
  if (meta) {
    meta.setAttribute('content', BACKGROUND[effectiveTheme(choice)])
    meta.setAttribute('media', 'all')
  }
}
